/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */

// https://material-ui.com/customization/themes/
import {createTheme} from '@material-ui/core/styles';
import merge from 'lodash.merge';
import {warn} from '@ion/core/src/logger/logger';
import {GenericObject} from '@ion/core/src/types/entities';
import {deepCopy} from '@ion/core/src/utils/ObjectUtil';
import {DLSTheme, IconSize} from '../types/types';
// @ts-ignore
import json from './theme.json5';
import loadExternalTheme from './ThemeLoader';

/**
 * The theme module defines the Material UI theme used by the application,
 * and exports all theme styles which are based on the iOn Suite UX / UI
 * Design Language System (iOn DLS). Please refer to the documentation below
 * for details:
 * https://www.figma.com/file/srjlHrDuM90sCoaOwpjaDU/02_DLS_Components?node-id=4%3A2
 *
 * Material UI based theme styles are available via their respective theme
 * names. For a complete list, please refer to the Material UI docs default
 * theme below:
 * https://github.com/callemall/material-ui/blob/master/src/styles/getMuiTheme.js
 *
 * Likewise, application specific styles which are not exposed as Material
 * UI theme names are provided via  the "custom" property of the theme.json
 * document, and are exported via the theme module as well.
 *
 * This theme module simply exports themes styles, effectively making them
 * available to the Application container as well as to all components within
 * the application. The actual theme styles and definitions are defined with
 * the corresponding theme.json document. The JSON document affords the ability
 * to use the same theme in both JavaScript as well as within SASS.
 *
 * As mentioned, all styles are defined within the corresponding theme.json
 * document, and are added to the exported Material UI theme via this module.
 * As such, Presentational Components can be wrapped in muiThemeable and
 * subsequently consume styles explicitly via props.theme
 *
 * ```typescript
 *   import {dlsLayout} from '@ion/dls/src/theme/theme';
 *   const {mobile, desktop} = dlsLayout('breakpoints'); // 414, 1440
 *   // or ...
 *   const breakpoint = dlsLayout('breakpoints', 'mobile'); // 414, 1440
 *
 * ```typescript
 *   import {dlsColor} from '@ion/dls/src/theme/theme';
 *   const color = dlsColor('caBlue'); // '#02ABCA'
 *
 * ```
 *
 * ```typescript
 *   import {dlsTypography} from '@ion/dls/src/theme/theme';
 *   const {fontFamily, fontWeight, fontSize, lineHeight} = dlsTypography('h1');
 *
 * ```
 *
 * ```typescript
 *   import React from 'react';
 *   import {withTheme} from '@material-ui/core/styles';
 *
 *   const ExampleComponent = ({theme}) => (
 *     // reference default.json "custom.primaryColor" style ...
 *     <span style={{color: theme.palette.primary}}>
 *        Example!
 *     </span>
 *   );
 *   export default withTheme(ExampleComponent);
 *
 * ```
 *
 * @module
 *
 */

/**
 * Provides the top-level exports of the theme, the dls object, which encompasses
 * the entire iOn Suite Design Language System, and the palette object, which
 * encompasses all Material UI theming definitions.
 */
export let {dls, palette} = json;

/**
 * Returns the given Color for the DLS color entry, see the link below for all
 * valid values.
 *
 * ```typescript
 *   const color = dlsColor('caBlue');   // '#02ABCA'
 *   const color = dlsColor('darkTeal'); // '#0C6178'
 *
 * ```
 *
 * @method dlsColor
 * @see https://www.figma.com/file/gtF4hB3eC4fvzKVJ2DNDtu/01_DLS_Foundation?node-id=1%3A167
 *
 */
export const dlsColor = (color: string) => {
  const val = dls.colors[color];
  if (!val) {
    logger('color', color);
  }
  return val;
};

/**
 * Returns the given Layout category for the DLS layout entry, see the link below
 * for all valid values.
 *
 * ```typescript
 *   const {mobile, desktop} = dlsLayout('breakpoints'); // 414, 1440
 *   const {mobile, desktop} = dlsLayout('gutters'); // 16, 24
 *   const columns = dlsLayout('columns'); // 12
 *   const margins = dlsLayout('margins'); // 24
 *   const spacing = dlsLayout('spacing'); // 8
 *
 * ```
 *
 * @method dlsLayout
 * @see https://www.figma.com/file/gtF4hB3eC4fvzKVJ2DNDtu/01_DLS_Foundation?node-id=207%3A6
 *
 */
export const dlsLayout = (...args: string[]) => {
  const val = getEntry('layout', ...args);
  if (!val) {
    logger('layout', ...args);
  }
  return val;
};

/**
 * Returns the given Typography category for the DLS typography entry, see the
 * link below for all valid values.
 *
 * ```typescript
 *   const {fontFamily, fontWeight, fontSize, lineHeight} = dlsTypography('h1');
 *
 * ```
 *
 * @see https://www.figma.com/file/gtF4hB3eC4fvzKVJ2DNDtu/01_DLS_Foundation?node-id=1244%3A34
 */
export const dlsTypography = (...args: string[]) => {
  const val = getEntry('typography', ...args);
  if (!val) {
    logger('typography', ...args);
  }
  return val;
};

/**
 * Returns the icon color based on the provided icon size for the DLS icon entry,
 * see the link below for valid values.
 *
 * ```typescript
 *   const color = dlsIconColor(IconSize.large);  // grey3 (see dls.colors.grey3)
 *   const color = dlsIconColor(IconSize.small);  // grey2 (see dls.colors.grey2)
 *   const color = dlsIconColor(IconSize.medium); // grey2 (see dls.colors.grey2)
 *
 * ```
 *
 * @see https://www.figma.com/file/gtF4hB3eC4fvzKVJ2DNDtu/00_DLS_Foundation?node-id=1%3A172
 *
 */
export const dlsIconColor = (size?: IconSize) => {
  return dlsColor(size === IconSize.large ? 'grey4' : 'grey3');
};

/**
 *  Returns the zindex for corresponding component names.
 *
 * ```typescript
 *   const color = dlsZIndex('assetHistoryTitle');
 *   const color = dlsZIndex('navigationDiv');
 *
 * ```
 */
export const dlsZIndex = (zindex: string) => {
  return dls.zIndices[zindex] || dls.zIndices.defaultZIndex;
};

/**
 * Returns the given non-standard DLS definitions as defined within the
 * alt section of the dls entry in theme.json5.
 *
 * The alt section defines all alternative DLS specifications which are
 * non-standard, as, in some cases, specific components and or features
 * within the DLS may define colors, sizes, typography, etc. which does
 * not strictly conform to the general DLS specifications. In all cases,
 * such non-standard definitions are to be provided via the alt section
 * of the dls entry.
 *
 * ```typescript
 *   const {disabled} = dlsAlt('btn');
 *
 * ```
 */
export const dlsAlt = (...args: string[]) => {
  const val = getEntry('alt', ...args);
  if (!val) {
    logger('alternative', ...args);
  }
  return val;
};

/**
 *
 * Provides a utility which allows for conditionally returning one of two DLS
 * colors.
 *
 * ```typescript
 *   const color = dlsColorCondition(isAsset, 'caBlue' : 'caGreen');
 * ```
 */
export const dlsColorCondition = (condition: boolean, colorTrue: string, colorFalse: string): string => {
  return dlsColor(condition ? colorTrue : colorFalse);
};

/**
 * @private
 *
 * Provides a helper function which iterates through a specific dls entry and
 * returns the value.
 */
const getEntry = (...args: string[]) => {
  let val: any;
  for (const arg of args) {
    val = (val || dls)[arg];
  }
  return val;
};

/**
 * @private
 */
const logger = (context, ...args: string[]) => {
  warn(
    `iOn DLS: Invalid DLS ${context} requested [${args.toString()}], please refer to the ${context} definitions defined in theme.json5.`
  );
};

/**
 * Creates the default Material UI DLS Theme used by all iOn Class of Applications.
 * As noted, by default, all iOn Applications are to use the default theme unless
 * white labeling is required, in which case, the createCustomTheme API is to be
 * used exclusively.
 *
 * The following outlines the process for using the default iOn DLS theme within
 * an application:
 *
 * ```typescript
 *   // src/pages/_app.tsx
 *
 *   // 1.) import default theme factory function ...
 *   import createDefaultTheme from '@ion/dls/src/theme/theme';
 *
 *   // 2.) create a reference to the default application theme ...
 *   const theme = createDefaultTheme();
 *
 *   // 3.) Set the theme in the application ThemeProvider ...
 *   function Application({...}: AppProps) {
 *     ...
 *     // set theme via createCustomTheme with application specific theme.json5
 *     return (
 *       <I18nextProvider i18n={i18n}>
 *         <ThemeProvider theme={theme}>
 *           <ErrorBoundary>
 *             <Component {...pageProps} />
 *           </ErrorBoundary>
 *         </ThemeProvider>
 *       </I18nextProvider>
 *     );
 *   };
 * ```
 * @see createDefaultTheme
 * @returns A Material UI Theme which implements the iOn DLS.
 */
const createDefaultTheme = (): DLSTheme => {
  return createCustomTheme(json, false);
};

/**
 * Allows for providing an overridden theme for supporting application white
 * labeling. In general, applications should use the default theme via the
 * default export in order to ensure compatibility and strict adherence to
 * the CalAmp DLS. Applications can use the createCustomTheme API to allow for
 * white labeling as well as for theme switching if required.
 *
 * It is important to note that, as with any overridden values, custom themes
 * must have the exact same API (same property structure) as the default DLS
 * theme.json5 document. Additionally, project specific theme keys and values
 * can be defined if needed. This essentially allows for extended a theme as
 * well as inheriting from the default theme.
 *
 * The following outlines the typical usage for implementing a custom theme.
 *
 * First, define a custom `theme.json5` document, providing the given overrides
 * as needed:
 *
 * ```typescript
 *   // src/theme/theme.json5
 *   {
 *     dls: {
 *       // Defines all White Labeled Color overrides
 *       colors: {
 *        aaBlue : '#000000'
 *       }
 *       ... // define all other relevant overrides ...
 *     }
 *   }
 * ```
 *
 * Next, create the applications theme module and export the custom theme:
 *
 * ```typescript
 *   // src/theme/theme.ts
 *   import {createCustomTheme} from '@ion/dls/src/theme/theme';
 *   import json from './theme.json5';
 *
 *   export default createCustomTheme(json);
 * ```
 *
 * Then, apply the theme to the application:
 *
 * ```typescript
 *   // src/pages/_app.tsx
 *
 *   // import custom theme ...
 *   import theme from '../theme/theme';
 *
 *   // import white labelled font overrides (if needed, otherwise import DLS fonts)
 *   // note: if fonts will be white labeled, add them to the applications /public
 *   // directory, for example /public/assets/fonts
 *   import '../fonts/fonts.scss';
 *
 *   // if  white labelled font overrides are not needed import DLS fonts ...
 *   import '@ion/dls/src/fonts/fonts.scss';
 *
 *   // import shared application styles ...
 *   import '@ion/applications/src/styles/index.scss';
 *   ...
 *
 *   // set theme via createCustomTheme with application specific theme.json5
 *   return (
 *     <I18nextProvider i18n={i18n}>
 *       <ThemeProvider theme={theme}>
 *         <ErrorBoundary>
 *           <Component {...pageProps} />
 *         </ErrorBoundary>
 *       </ThemeProvider>
 *     </I18nextProvider>
 *   );
 * ```
 *
 * @param   overrides Defines the JSON5 document which implements the DLS theme
 *          JSON5 API.
 * @param   includeDefaults When true, indicates that the existing DLS theme.json5
 *          is to be merged with the override JSON5 document, allowing for providing
 *          a subset of overrides as needed. This parameter defaults to true as
 *          applications are encouraged to only provide specific overrides where
 *          required in order to reduce redundancy while also leveraging all new
 *          updates defined within the DLS.
 * @returns A Material UI Theme which implements the iOn DLS.
 */
export const createCustomTheme = (overrides: GenericObject, includeDefaults = true): DLSTheme => {
  const theme = includeDefaults ? merge(deepCopy(json), overrides) : overrides;
  dls     = theme.dls;
  palette = theme.palette;
  return createTheme(theme) as DLSTheme;
};

/**
 * The `createExternalTheme` API allows for retrieving an externally defined theme
 * and generating an applications theme based on the retrieved theme.json document.
 *
 * This API is similar to the `createCustomTheme` API in that it allows for creating
 * a custom theme and / or merging the default theme, with the main difference being
 * that the `createExternalTheme` API allows for retrieving the custom theme from an
 * external resource (URL).
 *
 * This API is primarily used for providing application white-labelling, whereas the
 * `createCustomTheme` API is generally used for **CalAmp** managed applications which
 * require overriding the default theme.
 *
 * ```typescript
 *   // src/pages/_app.tsx
 *   import {createExternalTheme} from '@ion/dls/src/theme/theme';
 *
 *   ...
 *
 *   // store a reference to the theme in state ...
 *   const [theme, setTheme] = useState<DLSTheme|null>(null);
 *
 *   // on mount retrieve and create the external theme ...
 *   useEffect(() => {
 *     (async() => {
 *       const theme = await createExternalTheme('/theme.json');
 *       setTheme(theme);
 *      })();
 *   }, [])
 *
 *   return (
 *     <I18nextProvider i18n={i18n}>
 *       <ThemeProvider theme={theme}>
 *         <ErrorBoundary>
 *           <Component {...pageProps} />
 *         </ErrorBoundary>
 *       </ThemeProvider>
 *     </I18nextProvider>
 *   );
 * ```
 * *Important Note*: While the above demonstrates how the `createExternalTheme` API
 * can be used directly, clients should generally use the `useExternalTheme` hook as
 * it not only encapsulates the above implementation, but provides additional utility
 * such as release retrieval caching, local persistence caching, and more.
 *
 * @param   url The url from which the external theme is to be loaded.
 * @param   includeDefaults When true, indicates that the existing DLS theme.json5
 *          is to be merged with the override JSON5 document, allowing for providing
 *          a subset of overrides as needed. This parameter defaults to true as
 *          applications are encouraged to only provide specific overrides where
 *          required in order to reduce redundancy while also leveraging all new
 *          updates defined within the DLS.
 * @returns A Material UI Theme which implements the iOn DLS.
 */
export const createExternalTheme = async(url: string, includeDefaults = true): Promise<DLSTheme> => {
  const json = await loadExternalTheme(url);
  return createCustomTheme(json, includeDefaults);
};

/**
 * Provides the default export, which returns a created instance of the Material UI
 * theme based on the pre-defined theme.json5 document DLS specifications.
 *
 * https://github.com/callemall/material-ui/blob/master/src/styles/getMuiTheme.js
 */
export default createDefaultTheme;
